import PropTypes from 'prop-types';
import { Card, Table } from 'react-bootstrap';
import SubtleBadge from '../../components/common/SubtleBadge';
import formatDate from '../../utils/formatDate';

const STATUS_META = {
	waitingApproval: { bg: 'warning', text: '待审批', dot: 'bg-warning' },
	approved: { bg: 'success', text: '已通过', dot: 'bg-success' },
	reject: { bg: 'danger', text: '已驳回', dot: 'bg-danger' },
	withdrawn: { bg: 'secondary', text: '已撤回', dot: 'bg-secondary' },
};

const historyItemShape = PropTypes.shape({
	_id: PropTypes.string,
	status: PropTypes.string,
	approvalSuggestions: PropTypes.string,
	createdAt: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
	updatedAt: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
	applicant: PropTypes.shape({ name: PropTypes.string }),
	approver: PropTypes.shape({ name: PropTypes.string }),
	detail: PropTypes.shape({ orderId: PropTypes.string }),
});

const ApprovalHistoryTable = ({ history, compactPadding = false }) => (
	<Table responsive borderless size='sm' className='fs-10 mb-0'>
		<thead className={compactPadding ? '' : 'bg-body-tertiary'}>
			<tr>
				<th className={`${compactPadding ? 'ps-0' : 'ps-3'} text-nowrap`}>轮次</th>
				<th className='text-nowrap'>当时单号</th>
				<th className='text-nowrap'>结果</th>
				<th className='text-nowrap'>申请人</th>
				<th className='text-nowrap'>审批人</th>
				<th className='text-nowrap'>时间</th>
				<th className={compactPadding ? 'pe-0' : 'pe-3'}>驳回原因</th>
			</tr>
		</thead>
		<tbody>
			{history.map((item, index) => {
				const statusMeta = STATUS_META[item?.status] || { bg: 'secondary', text: item?.status || '—' };
				const thenOrderId = item?.detail?.orderId ? String(item.detail.orderId) : '—';
				const timeValue = item?.status === 'waitingApproval' ? item?.createdAt : item?.updatedAt || item?.createdAt;
				return (
					<tr key={item?._id || `approval-round-${index}`} className='border-top border-200'>
						<td className={`${compactPadding ? 'ps-0' : 'ps-3'} align-middle text-nowrap`}>第 {index + 1} 次</td>
						<td className='align-middle text-nowrap'>{thenOrderId}</td>
						<td className='align-middle'>
							<SubtleBadge pill bg={statusMeta.bg} className='fs-11'>
								{statusMeta.text}
							</SubtleBadge>
						</td>
						<td className='align-middle'>{item?.applicant?.name || '—'}</td>
						<td className='align-middle'>{item?.approver?.name || '—'}</td>
						<td className='align-middle text-nowrap'>{formatDate(timeValue)}</td>
						<td className={`${compactPadding ? 'pe-0' : 'pe-3'} align-middle`}>
							{item?.status === 'reject' ? (
								<span className='text-break'>{item?.approvalSuggestions || '未填写驳回说明'}</span>
							) : (
								'—'
							)}
						</td>
					</tr>
				);
			})}
		</tbody>
	</Table>
);

ApprovalHistoryTable.propTypes = {
	history: PropTypes.arrayOf(historyItemShape).isRequired,
	compactPadding: PropTypes.bool,
};

/** 右侧聚焦区：竖向审批时间线 */
const ApprovalHistoryTimeline = ({ history, className = '', heading = '审批历史' }) => (
	<div className={className}>
		{heading ? <p className='mb-2 fs-10 fw-semibold text-600'>{heading}</p> : null}
		<ul className='list-unstyled mb-0'>
			{history.map((item, index) => {
				const statusMeta = STATUS_META[item?.status] || { bg: 'secondary', text: item?.status || '—', dot: 'bg-secondary' };
				const thenOrderId = item?.detail?.orderId ? String(item.detail.orderId) : '';
				const timeValue = item?.status === 'waitingApproval' ? item?.createdAt : item?.updatedAt || item?.createdAt;
				const rejectReason =
					item?.status === 'reject' ? String(item?.approvalSuggestions || '').trim() || '未填写驳回说明' : '';
				const isLast = index === history.length - 1;
				return (
					<li key={item?._id || `approval-round-${index}`} className='d-flex gap-2'>
						<div className='d-flex flex-column align-items-center flex-shrink-0' style={{ width: 12 }}>
							<span className={`rounded-circle flex-shrink-0 ${statusMeta.dot || 'bg-secondary'}`} style={{ width: 8, height: 8, marginTop: 5 }} />
							{!isLast ? <span className='flex-grow-1 border-start border-300' style={{ width: 0, minHeight: 12 }} /> : null}
						</div>
						<div className={`min-w-0 flex-grow-1 ${isLast ? '' : 'pb-3'}`}>
							<div className='d-flex align-items-center flex-wrap gap-1 mb-1'>
								<span className='fs-10 fw-semibold text-nowrap'>第 {index + 1} 次</span>
								<SubtleBadge pill bg={statusMeta.bg} className='fs-11'>
									{statusMeta.text}
								</SubtleBadge>
							</div>
							<div className='fs-11 text-500'>
								{formatDate(timeValue)}
								{item?.approver?.name ? ` · ${item.approver.name}` : ''}
							</div>
							{thenOrderId ? <div className='fs-11 text-500 text-truncate'>单号 {thenOrderId}</div> : null}
							{rejectReason ? <div className='fs-10 text-danger text-break mt-1'>{rejectReason}</div> : null}
						</div>
					</li>
				);
			})}
		</ul>
	</div>
);

ApprovalHistoryTimeline.propTypes = {
	history: PropTypes.arrayOf(historyItemShape).isRequired,
	className: PropTypes.string,
	heading: PropTypes.string,
};

/** 订单详情：按下单审批历次记录（按 orderOID / 订单 _id 关联） */
const OrderApprovalHistory = ({ history, className = 'mb-3', embedded = false, titleRow = false, heading }) => {
	if (!Array.isArray(history) || history.length === 0) return null;

	if (titleRow) {
		return <ApprovalHistoryTimeline history={history} className={className} heading={heading} />;
	}

	if (embedded) {
		return (
			<div className={className}>
				<p className='mb-2 fs-10 fw-semibold'>审批历史</p>
				<ApprovalHistoryTable history={history} compactPadding />
			</div>
		);
	}

	return (
		<Card className={className}>
			<Card.Header className='bg-body-tertiary'>
				<Card.Title as='div' className='mb-0'>
					<h6 className='mb-0'>审批历史</h6>
				</Card.Title>
			</Card.Header>
			<Card.Body className='p-0'>
				<ApprovalHistoryTable history={history} />
			</Card.Body>
		</Card>
	);
};

OrderApprovalHistory.propTypes = {
	history: PropTypes.arrayOf(historyItemShape),
	className: PropTypes.string,
	embedded: PropTypes.bool,
	titleRow: PropTypes.bool,
	heading: PropTypes.string,
};

export default OrderApprovalHistory;
